import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../services/firebase';
import { User } from '../types';

// Domain restriction for email accounts
const ALLOWED_DOMAIN = 'seagleart.com';
const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true' ||
                     process.env.NODE_ENV === 'development';
const isRegistrationEnabled = process.env.REACT_APP_REGISTRATION_ENABLED === 'true';
const requireEmailVerification = isRegistrationEnabled && !useEmulators;

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  resendVerificationEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isStaff: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set a timeout to stop loading after 5 seconds if Firebase doesn't respond
    const loadingTimeout = setTimeout(() => {
      console.warn('Firebase auth initialization timed out');
      setLoading(false);
    }, 5000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      try {
        if (firebaseUser) {
          // Block unverified email/password users (skip in emulator mode)
          const isPasswordUser = firebaseUser.providerData.some(p => p.providerId === 'password');
          if (isPasswordUser && !firebaseUser.emailVerified && requireEmailVerification) {
            await firebaseSignOut(auth);
            setCurrentUser(null);
            setLoading(false);
            clearTimeout(loadingTimeout);
            return;
          }

          // Get or create user document
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

          if (userDoc.exists()) {
            setCurrentUser(userDoc.data() as User);
          } else {
            // Create new user document - only include photoURL if it exists
            const newUser: User = {
              uid: firebaseUser.uid,
              email: firebaseUser.email!,
              displayName: firebaseUser.displayName || firebaseUser.email!.split('@')[0],
              ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL }),
              role: 'student',
              createdAt: new Date()
            };

            await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
            setCurrentUser(newUser);
          }
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
        clearTimeout(loadingTimeout);
      }
    });

    return () => {
      unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const validateEmailDomain = (email: string): boolean => {
    // In development/emulator mode, allow any domain
    if (useEmulators) {
      return true;
    }
    const domain = email.split('@')[1];
    return domain === ALLOWED_DOMAIN;
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      if (!validateEmailDomain(email)) {
        throw new Error(`Only @${ALLOWED_DOMAIN} email addresses are allowed`);
      }
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check email verification (only when registration is enabled)
      if (requireEmailVerification && !userCredential.user.emailVerified) {
        await firebaseSignOut(auth);
        throw new Error('Please verify your email before signing in. Check your inbox for a verification link.');
      }
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    try {
      if (!validateEmailDomain(email)) {
        throw new Error(`Only @${ALLOWED_DOMAIN} email addresses are allowed`);
      }
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName });

      // Send verification email and sign out (only when registration requires it)
      if (requireEmailVerification) {
        await sendEmailVerification(userCredential.user);
        await firebaseSignOut(auth);
      }
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  const resendVerificationEmail = async (email: string, password: string) => {
    try {
      if (!validateEmailDomain(email)) {
        throw new Error(`Only @${ALLOWED_DOMAIN} email addresses are allowed`);
      }
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (!userCredential.user.emailVerified) {
        await sendEmailVerification(userCredential.user);
      }
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error resending verification email:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const isAdmin = currentUser?.role === 'admin';
  const isStaff = currentUser?.role === 'staff' || isAdmin;

  const value = {
    currentUser,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resendVerificationEmail,
    signOut,
    isAdmin,
    isStaff
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};